import {
  COMPONENT_TYPE_IMAGE,
  COMPONENT_TYPE_LOGO,
  COMPONENT_TYPE_TEXT,
  COMPONENT_TYPE_GUEST,
  COMPONENT_TYPE_TITLE,
  COMPONENT_TYPE_CHECKBOX,
  COMPONENT_TYPE_RADIOBUTTON,
  COMPONENT_TYPE_BUTTON,
  FormWelcome,
  useDragAndDrop,
  useFetchElementsTemplates,
  FormComponent,
  FormData,
  getTextProperties,
  Guests,
  Form,
} from '@entities/leadCollector';
import {
  FormAddGuest,
  FormButton,
  FormOptionGroup,
  FormGuests,
  FormHeader,
  FormText,
  FormImage,
  FormItemWrapper,
  FormLogo,
} from './ui';
import { PreloaderCircular } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface FormCanvasProps {
  formData?: FormData;
  onDeleteComponent?: (instanceId: string) => void;
  onReorder?: (dragIndex: number, dropIndex: number) => void;
  form?: Form;
  onChangeProperties: (index: string, properties: Record<string, unknown>) => void;
  isCanEdit: boolean;
  onSaveForm: () => void;
  isLoadingForm: boolean;
}

export const FormCanvas = ({
  formData,
  onDeleteComponent,
  onReorder,
  form,
  onChangeProperties,
  isCanEdit,
  onSaveForm,
  isLoadingForm,
}: FormCanvasProps) => {
  const { draggedIndex, draggedOverIndex, handleDragStart, handleDragEnd, handleDragOver, handleDrop } = useDragAndDrop(
    {
      onReorder,
    }
  );
  const { elementsTemplates } = useFetchElementsTemplates();
  const textProperties = getTextProperties(elementsTemplates);

  if (isLoadingForm) {
    return (
      <div {...stylex.props(styles.container, isCanEdit && styles.previewContainer, styles.loadingContainer)}>
        <p {...stylex.props(styles.loader)}>
          <PreloaderCircular />
        </p>
      </div>
    );
  }

  if (!formData?.components.length) {
    return <FormWelcome />;
  }

  const getComponent = (component: FormComponent) => {
    switch (component.type) {
      case COMPONENT_TYPE_LOGO:
        return <FormLogo type={Number(component.id)} />;
      case COMPONENT_TYPE_IMAGE: {
        return (
          <FormImage
            onDelete={onDeleteComponent}
            instanceId={component.instanceId}
            setValue={(file) => onChangeProperties(component.instanceId, { file })}
            value={(component.properties.file as File) || component.properties.src}
            isCanEdit={isCanEdit}
          />
        );
      }
      case COMPONENT_TYPE_GUEST:
        return (
          <FormGuests
            form={form}
            isCanEdit={isCanEdit}
            value={component.properties as unknown as Guests}
            onChange={(properties) => onChangeProperties(component.instanceId, { ...properties })}
          />
        );
      case COMPONENT_TYPE_TEXT:
        return (
          <FormText
            onChange={(html) => onChangeProperties(component.instanceId, { html })}
            properties={textProperties}
            isCanEdit={isCanEdit}
            content={(component.properties.html || '') as string}
          />
        );
      case COMPONENT_TYPE_TITLE:
        return (
          <FormHeader
            type={Number(component.id)}
            onChange={(headerTitle) => onChangeProperties(component.instanceId, { headerTitle })}
            isCanEdit={isCanEdit}
            value={(component.properties.headerTitle || '') as string}
          />
        );
      case COMPONENT_TYPE_CHECKBOX:
        return (
          <FormOptionGroup
            key={component.instanceId}
            onChange={(properties) => onChangeProperties(component.instanceId, { ...properties })}
            properties={textProperties}
            isCanEdit={isCanEdit}
            value={component.properties}
          />
        );
      case COMPONENT_TYPE_RADIOBUTTON:
        return (
          <FormOptionGroup
            key={component.instanceId}
            onChange={(properties) => onChangeProperties(component.instanceId, { ...properties })}
            properties={textProperties}
            isRadio
            isCanEdit={isCanEdit}
            value={component.properties}
          />
        );
      case COMPONENT_TYPE_BUTTON:
        return (
          <FormButton
            onChange={(textButton) => onChangeProperties(component.instanceId, { textButton })}
            isCanEdit={isCanEdit}
            onButtonClick={onSaveForm}
            value={component.properties}
          />
        );
    }
  };

  const getAddGuestComponent = (component: FormComponent) => (
    <FormAddGuest
      maxExtraAdults={form?.maxExtraAdults ?? 0}
      maxExtraChildren={form?.maxExtraChildren ?? 0}
      value={structuredClone(component.properties as unknown as Guests)}
      onChange={(properties) => onChangeProperties(component.instanceId, { ...properties })}
      isCanEdit={isCanEdit}
    />
  );

  const renderComponents = (component: FormComponent, index: number) => {
    if (isCanEdit) {
      return (
        <div key={component.instanceId} {...stylex.props(styles.containerPreview)}>
          {getComponent(component)}

          {component.type === COMPONENT_TYPE_GUEST && getAddGuestComponent(component)}
        </div>
      );
    }

    const isDragging = draggedIndex === index;
    const isDraggedOver = draggedOverIndex === index;
    const isHideHud = component.type === COMPONENT_TYPE_IMAGE;
    const isHideActions = component.type === COMPONENT_TYPE_IMAGE;

    return (
      <>
        <FormItemWrapper
          key={component.instanceId}
          instanceId={component.instanceId}
          onDelete={onDeleteComponent}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          dragStyles={[styles.draggableItem, isDragging && styles.draggableItemDragging]}
          isDraggedOver={isDraggedOver}
          isDragging={draggedIndex !== undefined}
          isHideHud={isHideHud}
          isHideActions={isHideActions}
        >
          {getComponent(component)}
        </FormItemWrapper>

        {component.type === COMPONENT_TYPE_GUEST && getAddGuestComponent(component)}
      </>
    );
  };

  return (
    <div {...stylex.props(styles.container, isCanEdit && styles.previewContainer)}>
      {formData?.components.map(renderComponents)}
    </div>
  );
};
